import { useState, useCallback, useMemo } from "react";
import { useDepletionMonthly, FilterMode, DepletionMonthlyStudent } from "@/hooks/useDepletionMonthly";
import { DepletionV2SummaryCards } from "./DepletionV2SummaryCards";
import { DepletionV2CrmCard } from "./DepletionV2CrmCard";
import { DepletionV2Table, SortFieldV2, SortDirection } from "./DepletionV2Table";
import { DepletionFilters } from "@/components/admin/depletion/DepletionFilters";
import { startOfMonth, endOfMonth, format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Filter } from "lucide-react";

const THAI_MONTHS = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
];

export function DepletionV2Content() {
  const now = new Date();
  const [filterMode, setFilterMode] = useState<FilterMode>('month');
  const [month, setMonth] = useState(() => {
    const saved = localStorage.getItem('depletion-v2-month');
    return saved ? Number(saved) : now.getMonth() + 1;
  });
  const [year, setYear] = useState(() => {
    const saved = localStorage.getItem('depletion-v2-year');
    return saved ? Number(saved) : now.getFullYear();
  });

  // Date range mode states
  const [startDate, setStartDate] = useState(() => startOfMonth(now));
  const [endDate, setEndDate] = useState(() => endOfMonth(now));
  const [queryStartDate, setQueryStartDate] = useState(() => format(startOfMonth(now), "yyyy-MM-dd"));
  const [queryEndDate, setQueryEndDate] = useState(() => format(endOfMonth(now), "yyyy-MM-dd"));

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [sortField, setSortField] = useState<SortFieldV2>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const { students, summary, isLoading, lastFetchedAt, refresh } = useDepletionMonthly({
    mode: filterMode,
    month,
    year,
    startDate: queryStartDate,
    endDate: queryEndDate,
  });

  const handlePrevMonth = useCallback(() => {
    setMonth(prev => {
      let newMonth: number;
      if (prev === 1) {
        setYear(y => { const ny = y - 1; localStorage.setItem('depletion-v2-year', String(ny)); return ny; });
        newMonth = 12;
      } else {
        newMonth = prev - 1;
      }
      localStorage.setItem('depletion-v2-month', String(newMonth));
      return newMonth;
    });
  }, []);

  const handleNextMonth = useCallback(() => {
    setMonth(prev => {
      let newMonth: number;
      if (prev === 12) {
        setYear(y => { const ny = y + 1; localStorage.setItem('depletion-v2-year', String(ny)); return ny; });
        newMonth = 1;
      } else {
        newMonth = prev + 1;
      }
      localStorage.setItem('depletion-v2-month', String(newMonth));
      return newMonth;
    });
  }, []);

  const handleDateSearch = useCallback(() => {
    setQueryStartDate(format(startDate, "yyyy-MM-dd"));
    setQueryEndDate(format(endDate, "yyyy-MM-dd"));
  }, [startDate, endDate]);

  const handleSort = useCallback((field: SortFieldV2) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortField(null);
        setSortDirection('asc');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField, sortDirection]);

  // Get unique statuses for filter dropdown
  const uniqueStatuses = useMemo(() => {
    const set = new Set<string>();
    students.forEach(s => set.add(s.status ?? 'ยังไม่ได้ระบุ'));
    return Array.from(set).sort();
  }, [students]);

  const processedStudents = useMemo(() => {
    let result = [...students];

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(s =>
        s.student_code.toLowerCase().includes(q) ||
        (s.first_name && s.first_name.toLowerCase().includes(q)) ||
        (s.last_name && s.last_name?.toLowerCase().includes(q)) ||
        (s.nickname && s.nickname.toLowerCase().includes(q))
      );
    }

    // Status filter (multiselect)
    if (statusFilter.length > 0) {
      result = result.filter(s => {
        const val = s.status ?? '__null__';
        return statusFilter.includes(val);
      });
    }

    // Sort
    if (sortField) {
      result.sort((a, b) => {
        let valA: string | number;
        let valB: string | number;

        switch (sortField) {
          case 'code':
            valA = a.student_code;
            valB = b.student_code;
            break;
          case 'estimated_hours_depletion_date':
            valA = a.estimated_hours_depletion_date ?? '';
            valB = b.estimated_hours_depletion_date ?? '';
            break;
          case 'estimated_8hours_date':
            valA = a.estimated_8hours_date ?? '';
            valB = b.estimated_8hours_date ?? '';
            break;
          case 'hours':
            valA = a.hours ?? -Infinity;
            valB = b.hours ?? -Infinity;
            break;
          default:
            return 0;
        }

        if (sortDirection === 'asc') {
          return valA < valB ? -1 : valA > valB ? 1 : 0;
        } else {
          return valA > valB ? -1 : valA < valB ? 1 : 0;
        }
      });
    }

    return result;
  }, [students, searchQuery, statusFilter, sortField, sortDirection]);

  return (
    <div className="space-y-6">
      {/* Filter Bar */}
      <div className="bg-card border rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Label htmlFor="mode-toggle" className="text-sm text-muted-foreground whitespace-nowrap">เลือกช่วงวัน</Label>
            <Switch
              id="mode-toggle"
              checked={filterMode === 'dateRange'}
              onCheckedChange={(checked) => setFilterMode(checked ? 'dateRange' : 'month')}
            />
          </div>
          <div className="flex items-center gap-2">
            {lastFetchedAt && (
              <span className="text-xs text-muted-foreground">
                อัปเดตล่าสุด: {lastFetchedAt.toLocaleTimeString('th-TH')}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={refresh} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
              รีเฟรช
            </Button>
          </div>
        </div>

        {filterMode === 'month' ? (
          <div className="flex items-center gap-3">
            <Button variant="outline" size="icon" onClick={handlePrevMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-lg font-semibold min-w-[180px] text-center">
              {THAI_MONTHS[month - 1]} {year}
            </span>
            <Button variant="outline" size="icon" onClick={handleNextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <DepletionFilters
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onSearch={handleDateSearch}
            onRefresh={refresh}
            lastFetchedAt={lastFetchedAt}
            isLoading={isLoading}
          />
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid gap-6 md:grid-cols-2">
        <DepletionV2SummaryCards summary={summary} isLoading={isLoading} />
        <DepletionV2CrmCard statusBreakdown={summary.status_breakdown} total={summary.total} isLoading={isLoading} />
      </div>

      {/* Search + Filter Bar */}
      <div className="flex flex-wrap items-center gap-4 bg-card border rounded-lg p-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="ค้นหา (รหัส, ชื่อ, ชื่อเล่น)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <Filter className="h-4 w-4" />
              สถานะ
              {statusFilter.length > 0 && (
                <span className="bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 text-xs">
                  {statusFilter.length}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2" align="start">
            <div className="space-y-1">
              <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm">
                <Checkbox
                  checked={statusFilter.includes('__null__')}
                  onCheckedChange={(checked) => {
                    setStatusFilter(prev => checked ? [...prev, '__null__'] : prev.filter(v => v !== '__null__'));
                  }}
                />
                ยังไม่ได้ระบุ
              </label>
              {uniqueStatuses.filter(s => s !== 'ยังไม่ได้ระบุ').map(s => (
                <label key={s} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm">
                  <Checkbox
                    checked={statusFilter.includes(s)}
                    onCheckedChange={(checked) => {
                      setStatusFilter(prev => checked ? [...prev, s] : prev.filter(v => v !== s));
                    }}
                  />
                  {s}
                </label>
              ))}
              {statusFilter.length > 0 && (
                <Button variant="ghost" size="sm" className="w-full mt-1" onClick={() => setStatusFilter([])}>
                  ล้างตัวกรอง
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>
        <div className="ml-auto text-sm text-muted-foreground">
          แสดง {processedStudents.length} จาก {students.length} รายการ
        </div>
      </div>

      {/* Table */}
      <DepletionV2Table
        students={processedStudents}
        isLoading={isLoading}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
      />
    </div>
  );
}
